import type { User, UserRoleType } from '@/lib/types';
import { SAMPLE_USER_ORGANIZATION_MAPPINGS } from './userOrganizationMapping';

/**
 * Sample users data for demo purposes
 * These users correspond to the UserOrganizationMapping data
 * 
 * GUEST USER FUNCTIONALITY:
 * - Guest (guest@example.com) has restricted access with Guest role
 * - Guest users can only access: Dashboard, Calendar, and Bookings pages
 * - Guest users cannot see Admin or Super Admin navigation sections
 * - Guest users have limited permissions: bookings.view and bookings.create
 * - Password for all sample users: SamplePassword123!
 */
export const sampleUsers: User[] = SAMPLE_USER_ORGANIZATION_MAPPINGS.map(mapping => ({
  uid: mapping.uid,
  email: mapping.email,
  displayName: mapping.displayName,
  firstName: mapping.firstName,
  lastName: mapping.lastName,
  roles: mapping.roles,
  status: 'Active',
  phoneNumber: getPhoneNumberForUser(mapping.uid),
  photoURL: '/user.png', // Always use fallback image
  gender: getGenderForUser(mapping.uid),
  preferredLanguage: getPreferredLanguageForUser(mapping.uid),
  dateOfBirth: getDateOfBirthForUser(mapping.uid),
  organizationId: mapping.organizationId,
  tenantId: mapping.tenantId,
  associatedClubIds: mapping.associatedClubIds || [],
  creationTime: getCreationTimeForUser(mapping.uid),
  lastSignInTime: getLastSignInTimeForUser(mapping.uid),
  providerData: [{
    uid: mapping.uid,
    email: mapping.email,
    displayName: mapping.displayName,
    providerId: 'firebase'
  }],
  customClaims: {
    roles: mapping.roles,
    organizationId: mapping.organizationId,
    tenantId: mapping.tenantId,
    firstName: mapping.firstName,
    lastName: mapping.lastName
  },
  dateCreated: getCreationTimeForUser(mapping.uid),
  dateUpdated: getLastSignInTimeForUser(mapping.uid),
  address: getAddressForUser(mapping.uid),
  billingAddress: getBillingAddressForUser(mapping.uid)
}));

/**
 * Get address data for sample users
 */
function getAddressForUser(uid: string) {
  const addresses: Record<string, {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    postalCode: string;
    stateOrProvince: string;
    country: string;
  }> = {
    '2847391650': { // Roger Federer
      addressLine1: '123 Tennis Court Avenue',
      addressLine2: 'Suite 1',
      city: 'Basel',
      postalCode: '4001',
      stateOrProvince: 'Basel-Stadt',
      country: 'Switzerland',
    },
    '4567891230': { // John McEnroe
      addressLine1: '456 Champion Street',
      city: 'New York',
      postalCode: '10001',
      stateOrProvince: 'New York',
      country: 'United States',
    },
    '5678912340': { // Venus Williams
      addressLine1: '789 Wimbledon Way',
      addressLine2: 'Apt 5B',
      city: 'Miami',
      postalCode: '33101',
      stateOrProvince: 'Florida',
      country: 'United States',
    },
    '6789123450': { // Amélie Mauresmo
      addressLine1: '321 Roland Garros Boulevard',
      city: 'Paris',
      postalCode: '75016',
      stateOrProvince: 'Île-de-France',
      country: 'France',
    },
    'superadmin001': { // Laurent Christen
      addressLine1: '654 Innovation Drive',
      addressLine2: 'Building C',
      city: 'Sierre',
      postalCode: '8001',
      stateOrProvince: 'Valais',
      country: 'Switzerland',
    },
    '4051728396': { // Maria Sharapova
      addressLine1: '5650 Bollettieri Boulevard',
      addressLine2: 'IMG Academy',
      city: 'Bradenton',
      postalCode: '34210',
      stateOrProvince: 'Florida',
      country: 'United States',
    },
    '9182736450': { // Guest
      addressLine1: '456 Guest Lane',
      city: 'Geneva',
      postalCode: '1201',
      stateOrProvince: 'Geneva',
      country: 'Switzerland',
    },
  };
  return addresses[uid];
}

/**
 * Get billing address data for sample users
 */
function getBillingAddressForUser(uid: string) {
  const billingAddresses: Record<string, {
    company?: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    postalCode: string;
    stateOrProvince: string;
    country: string;
    sameAsCorrespondence: boolean;
  }> = {
    '2847391650': { // Roger Federer
      company: 'RF Foundation',
      addressLine1: '123 Tennis Court Avenue',
      addressLine2: 'Suite 1',
      city: 'Basel',
      postalCode: '4001',
      stateOrProvince: 'Basel-Stadt',
      country: 'Switzerland',
      sameAsCorrespondence: true,
    },
    '4567891230': { // John McEnroe
      company: 'McEnroe Tennis Academy',
      addressLine1: '789 Business Plaza',
      city: 'New York',
      postalCode: '10002',
      stateOrProvince: 'New York',
      country: 'United States',
      sameAsCorrespondence: false,
    },
    '5678912340': { // Venus Williams
      company: 'V Starr Interiors',
      addressLine1: '789 Wimbledon Way',
      addressLine2: 'Apt 5B',
      city: 'Miami',
      postalCode: '33101',
      stateOrProvince: 'Florida',
      country: 'United States',
      sameAsCorrespondence: true,
    },
    '6789123450': { // Amélie Mauresmo
      addressLine1: '321 Roland Garros Boulevard',
      city: 'Paris',
      postalCode: '75016',
      stateOrProvince: 'Île-de-France',
      country: 'France',
      sameAsCorrespondence: true,
    },
    'superadmin001': { // Laurent Christen
      company: 'Dark Studio Digital Operations',
      addressLine1: '100 Corporate Center',
      city: 'Zurich',
      postalCode: '8002',
      stateOrProvince: 'Zurich',
      country: 'Switzerland',
      sameAsCorrespondence: false,
    },
    '4051728396': { // Maria Sharapova
      company: 'Sugarpova LLC',
      addressLine1: '1001 Brickell Bay Drive',
      addressLine2: 'Suite 2700',
      city: 'Miami',
      postalCode: '33131',
      stateOrProvince: 'Florida',
      country: 'United States',
      sameAsCorrespondence: false,
    },
    '9182736450': { // Guest
      addressLine1: '456 Guest Lane',
      city: 'Geneva',
      postalCode: '1201',
      stateOrProvince: 'Geneva',
      country: 'Switzerland',
      sameAsCorrespondence: true,
    },
  };
  return billingAddresses[uid];
}

/**
 * Get phone number for sample users
 */
function getPhoneNumberForUser(_uid: string): string | undefined {
  const phoneNumbers: Record<string, string> = {
    '2847391650': '+41 79 123 45 67', // Roger Federer (Swiss)
    '4567891230': '+34 600 123 456', // John McEnroe (Spain)
    '5678912340': '+1 555 123 4567', // Venus Williams (US)
    '6789123450': '+33 6 12 34 56 78', // Amélie Mauresmo (France)
    'superadmin001': '+41 79 999 99 99', // Laurent Christen (Swiss)
    '4051728396': '+41 78 123 45 67', // Maria Sharapova (Swiss)
    '9182736450': '+41 76 555 01 23', // Mark (Swiss)
  };
  return phoneNumbers[_uid];
}

/**
 * Get gender for sample users
 */
function getGenderForUser(_uid: string): 'Male' | 'Female' | 'Other' | 'Prefer not to say' | undefined {
  const genders: Record<string, 'Male' | 'Female' | 'Other' | 'Prefer not to say'> = {
    '2847391650': 'Male', // Roger Federer
    '4567891230': 'Male', // John McEnroe
    '5678912340': 'Female', // Venus Williams
    '6789123450': 'Female', // Amélie Mauresmo
    'superadmin001': 'Male', // Laurent Christen
    '4051728396': 'Female', // Maria Sharapova
    '9182736450': 'Male', // Guest
  };
  return genders[_uid];
}

/**
 * Get preferred language for sample users
 */
function getPreferredLanguageForUser(_uid: string): string | undefined {
  const languages: Record<string, string> = {
    '2847391650': 'de-CH', // Roger Federer (Swiss German)
    '4567891230': 'en-US', // John McEnroe (US English)
    '5678912340': 'en-US', // Venus Williams (US English)
    '6789123450': 'fr-FR', // Amélie Mauresmo (French)
    'superadmin001': 'en-GB', // Laurent Christen (UK English)
    '4051728396': 'en-US', // Maria Sharapova (US English)
    '9182736450': 'en-US', // Guest (US English)
  };
  return languages[_uid];
}

/**
 * Get date of birth for sample users
 */
function getDateOfBirthForUser(_uid: string): string | undefined {
  const birthDates: Record<string, string> = {
    '2847391650': '1981-08-08', // Roger Federer
    '4567891230': '1959-02-16', // John McEnroe
    '5678912340': '1980-06-17', // Venus Williams
    '6789123450': '1979-07-05', // Amélie Mauresmo
    'superadmin001': '2010-01-11', // Laurent Christen
    '4051728396': '1987-04-19', // Maria Sharapova
    '9182736450': '1990-03-15', // Guest
  };
  return birthDates[_uid];
}

/**
 * Get creation time for sample users
 */
function getCreationTimeForUser(_uid: string): string {
  // Sample creation times (older users have earlier dates)
  const creationTimes: Record<string, string> = {
    'superadmin001': '2023-01-15T10:00:00.000Z', // Platform admin created first
    '2847391650': '2023-02-01T14:30:00.000Z', // Roger Federer
    '4567891230': '2023-02-15T11:45:00.000Z', // John McEnroe
    '5678912340': '2023-03-01T16:20:00.000Z', // Venus Williams
    '6789123450': '2023-03-10T09:15:00.000Z', // Amélie Mauresmo
    '4051728396': '2023-03-20T13:40:00.000Z', // Maria Sharapova
    '9182736450': '2023-04-01T10:15:00.000Z', // Guest
  };
  return creationTimes[_uid] || new Date().toISOString();
}

/**
 * Get last sign in time for sample users
 */
function getLastSignInTimeForUser(_uid: string): string {
  // Recent activity (within last few days)
  const now = new Date();
  const daysAgo = Math.floor(Math.random() * 7); // 0-7 days ago
  const lastSignIn = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
  return lastSignIn.toISOString();
}

/**
 * Get all sample users
 */
export function getAllSampleUsers(): User[] {
  return sampleUsers;
}

/**
 * Get sample users for a specific organization
 */
export function getSampleUsersForOrganization(organizationId: string): User[] {
  return sampleUsers.filter(user => user.organizationId === organizationId);
}

/**
 * Get sample user by ID
 */
export function getSampleUserById(uid: string): User | null {
  return sampleUsers.find(user => user.uid === uid) || null;
}

/**
 * Get sample user by email
 */
export function getSampleUserByEmail(email: string): User | null {
  return sampleUsers.find(user => 
    user.email.toLowerCase() === email.toLowerCase()
  ) || null;
}

/**
 * Get sample users by role
 */
export function getSampleUsersByRole(role: string): User[] {
  return sampleUsers.filter(user => user.roles?.includes(role as UserRoleType));
} 
